import React, {
  memo, useMemo, useState, useEffect, useRef,
} from 'react';

import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  permissions as PERMISSIONS,
  ASYNC_JOB_STATUS_WAITING,
  ASYNC_JOB_STATUS_RUNNING,
  ASYNC_JOB_STATUS_COMPLETE,
  ASYNC_JOB_STATUS_FAILED,
  ASYNC_JOB_STATUS_REJECTED,
} from '@picsio/db/src/constants';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import cn from 'classnames';
import { Icon, Tooltip } from '@picsio/ui';
import {
  Cloud, Warning, Shopify, Product, AutoSuggestDown, AiEngine, FindImage,
} from '@picsio/icons';

import PermissionsChecker from '@picsio/db/src/helpers/PermissionsChecker';

import useHover from '../../helpers/components/hooks/useHover';
import getVideoThumbnail from '../../helpers/getVideoThumbnail';
import localization from '../../shared/strings';
import Logger from '../../services/Logger';
import picsioConfig from '../../../../../config';
import pollGDThumbnail from '../../helpers/thumbnailPoller';
import revisionUploader from '../../helpers/revisionUploader';
import checkForThumbnailing from '../../helpers/checkForThumbnailing';
import showDownloadDialog from '../../helpers/fileDownloader/showDownloadDialog';
import { checkUserAccess } from '../../store/helpers/user';
import { checkDownloadConsent } from '../../store/helpers/assets';
import * as utils from '../../shared/utils';
import ua from '../../ua';
import { navigate, setSearchRoute, getSearchProps } from '../../helpers/history';
import isCommentsHidden from '../../helpers/roles/isCommentsHidden';
import { getSavedCurrentTime } from '../previewView/views/Video/helpers';
import {
  checkIsAssetSelected,
  checkIsAssetHasNewComments,
  checkIsAssetHasNewRevisions,
  checkIsAssetSelectedToMatch,
} from '../../store/selectors/catalogView';
import {
  configErrors,
  configFormats,
  getConfigDefaultPlaceholder,
  configGApps,
} from './config';
import Archived from './Archived';
import Assignees from './Assignees';
import Collections from './Collections';
import Color from './Color';
import Comments from './Comments';
import Controls from './Controls';
import Checkbox from './Checkbox';
import Duration from './Duration';
import Transcription from './Transcription';
import Flag from './Flag';
import Keywords from './Keywords';
import Lightboards from './Lightboards';
import Media from './Media';
import MediaPlaceholder from './MediaPlaceholder';
import PlaceholderAssetBusy from './PlaceholderAssetBusy';
import Restriction from './Restriction';
import Revisions from './Revisions';
import Spinner from './Spinner';
import Stars from './Stars';
import TitleDescription from './TitleDescription';
import VideoProgress from './VideoProgress';
import {
  handleContextMenu,
  handleDragEnd,
  handleDragLeave,
  handleDragOver,
  handleDragStart,
  handleDrop,
  handleMouseDown,
  handleMouseUp,
  initTouchListeners,
  makeTransformForImage,
  normalizeAsset,
  removeTouchListeners,
} from './helpers';
import store from '../../store/index';
import Tag from '../Tag';
import './styles.scss';
import ButtonExternal from './ButtonExternal';
import convertAssetToMatchData from '../../store/helpers/assets/convertAssetToMatchData';
import ImageOrder from './ImageOrder';
import { findGalleryOrderEntry } from '../AssetTargetsDialog/helpers/assetProductLinks';
import isVisualSearchEnabled from '../../helpers/isVisualSearchEnabled';

dayjs.extend(customParseFormat);

const colorsByStatus = {
  DRAFT: '#D28853',
  ACTIVE: '#98EA8B',
  ARCHIVED: '#666666',
};
const VARIANT_COLOR = '#986DC2';

const isMobile = ua.browser.isNotDesktop();

window.dragElement = null;

// Wrapper component to prevent nodeRef from being passed to DOM element
const ChipsWrapper = React.forwardRef((props, ref) => {
  // eslint-disable-next-line no-unused-vars, react/prop-types
  const { nodeRef, ...restProps } = props;
  return <div ref={ref} {...restProps} />;
});

function CatalogItem(props) {
  const isMediaMatch = useSelector((state) => state.main.bulkMediaMatchViewMode);
  const { isChangingOrder } = useSelector((state) => state.products);
  const { collections } = useSelector((state) => state.collections);
  const user = useSelector((state) => state.user);
  const catalogItemRef = useRef(null);
  const inputFileRef = useRef(null);
  const controlsRef = useRef(null);
  const chipsRef = useRef(null);
  const isHovering = useHover(catalogItemRef, {
    enterDelay: 10,
    leaveDelay: 10,
  });
  const enableVisualSearchSetting = isVisualSearchEnabled(user);
  const visualSearchFeature = user?.subscriptionFeatures?.visualSearch;
  const searchProps = getSearchProps();

  const {
    asset,
    number,
    styles,
    isListViewMode,
    inProgress = false,
    assetsActions,
    isMobileView,
    productId = null,
    showImageOrder = false,
    imageOrderDisabled = false,
    imageOrderTooltip,
    viewContext = null,
    isOdd = false,
    isLightboardsView = false,
    isProductView = false,
    isVariant = false,
    removeDisabled = false,
    removeTooltip = 'Remove',
    activeCollectionSortType,
    activeCollectionIds,
    productAssetsLength = 0,
  } = props;

  const normalizedAsset = useMemo(() => normalizeAsset(asset, user), [
    asset,
    user,
  ]);
  const {
    deleteAssets,
    removeAsset,
    detachKeyword,
    removeFromLightboard,
    removeHighlight,
    removeNotFoundAssets,
    reorder,
    restoreAssets,
    select,
    selectByMatch,
    trashAssets,
    duplicateAssets,
    retryImporting,
  } = assetsActions;

  const {
    _id: assetId,
    allowAssetSharing,
    allowRemoveTags,
    archived,
    archivedByReason,
    assetSharing,
    assignees,
    color,
    colorAt,
    colorBy,
    colorChangeable,
    colorShow,
    allowDuplicateAsset,
    comments,
    customThumbnail,
    crawling,
    description,
    downloadFiles,
    enableEditor,
    fileExtension = '',
    fileSize,
    fileNameShow,
    flag,
    flagChangeable,
    flagShow,
    hasAccess,
    imageMediaMetadata,
    inbox,
    is3DModel,
    isDownloadable,
    isEditableInPicsioEditor,
    isEmpty,
    isGoingToDelete,
    isGoingToMove,
    isGoingToRestore,
    isGoingToTrash,
    isGoingToCopy,
    isGoingToCopyConvert,
    isGoogleDriveDocument,
    isRestricted,
    isShared,
    isSupportedVideo,
    isTrashed,
    isVideo,
    isExternal,
    keywords = [],
    lightboards = [],
    meta = {},
    mimeType,
    name,
    paramsForHighlight,
    rating,
    ratingChangeable,
    ratingShow,
    removeFiles,
    restrictedReason,
    revisions = [],
    shortName,
    storageCreatedAt,
    storageId,
    storageType,
    tags,
    product,
    thumbnail,
    thumbnailing,
    importing,
    importingMedia,
    thumbnailingReason,
    title,
    updatedAt,
    uploadFiles,
    uploadRevisionProgress,
    urlSmallThumb,
    userOrientation,
    videoPreviews,
    videoThumbnail,
    commentsEdit,
    commentsEnable,
    commentsShow,
    revisionsShow,
    watermark,
    foundWithVisualSearch,
    foundWithVisualSearchText,
    foundWithVisualSearchCLIP,
    CLIPEmbedding,
    isImage,
  } = normalizedAsset;

  const isSelected = useSelector(isMediaMatch ? checkIsAssetSelectedToMatch(asset) : checkIsAssetSelected(assetId));
  const newComments = useSelector(checkIsAssetHasNewComments(assetId));
  const newRevisions = useSelector(checkIsAssetHasNewRevisions(assetId));
  const { trashed } = useSelector((state) => state.router.location.query);
  const { isWebpSupported } = useSelector((state) => state.main);
  const { role } = useSelector((state) => state.user);
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const [thumbnailPlaceholder, setThumbnailPlaceholder] = useState(null);
  const [showSpinner, setShowSpinner] = useState(false);
  const [spinnerTitle, setSpinnerTitle] = useState(null);
  const [mouseOver, setMouseOver] = useState(false);
  const [, forceUpdate] = React.useState(0); // hook like forceUpdate
  const permissionChecker = new PermissionsChecker(role);
  const isImportingInProcess = [
    ASYNC_JOB_STATUS_WAITING,
    ASYNC_JOB_STATUS_RUNNING,
  ].includes(importing);
  const isImportingFailed = [
    ASYNC_JOB_STATUS_FAILED,
    ASYNC_JOB_STATUS_REJECTED,
  ].includes(importing);
  const isImportingMediaInProcess = [
    ASYNC_JOB_STATUS_WAITING,
    ASYNC_JOB_STATUS_RUNNING,
  ].includes(importingMedia);
  const isImportingMediaFailed = [
    ASYNC_JOB_STATUS_FAILED,
    ASYNC_JOB_STATUS_REJECTED,
  ].includes(importingMedia);

  const roles = useSelector((state) => state.roles.items);
  const teammateRoleId = useMemo(() => user.parent && user.parent.teammateRoleId, [user.parent]);
  const userRole = useMemo(() => roles.find((role) => role._id === teammateRoleId), [roles, teammateRoleId]);
  const hiddenFields = useMemo(() => userRole?.hiddenFields || [], [userRole?.hiddenFields]);
  const commentsHidden = useMemo(() => isCommentsHidden(userRole), [userRole]);

  const isAllowedLightboards = checkUserAccess(
    'permissions',
    'manageLightboards',
    null,
    user,
  );
  let videoThumbFetched = null;
  const isAssetBusy = isGoingToDelete
    || isGoingToRestore
    || isGoingToTrash
    || isGoingToMove
    || isGoingToCopy
    || isGoingToCopyConvert;
  const extension = fileExtension && fileExtension.toLowerCase();
  const isTransparentImageLoaded = thumbnailLoaded
    && (extension === 'png' || extension === 'svg' || extension === 'gif');
  const isAssetFromInbox = Boolean(inbox);
  const userTz = user?.settings?.timeZone?.userTimeZone;
  const createdDate = utils.formatWithUserZone(meta.CreateDate, userTz);
  const generatedDate = utils.formatWithUserZone(storageCreatedAt, userTz);
  const { Duration: duration } = meta;
  const videoProgress = isVideo && duration ? getSavedCurrentTime(assetId) : null;

  const showTileHint = picsioConfig.isMainApp
    || (picsioConfig.access
      && (picsioConfig.access.flagShow
        || picsioConfig.access.ratingShow
        || picsioConfig.access.colorShow
        || picsioConfig.access.download
        || picsioConfig.access.fileNameShow
        || generatedDate
        || createdDate));

  const url = thumbnail ? thumbnail.small : null;
  const poller = useRef(null);

  const handleDeleteForever = () => {
    Logger.log('User', 'ThubmnailDeleteAssetsFromTrashDialog');
    const deleteWithoutTrash = (isEmpty && !isTrashed) || isAssetFromInbox || false;
    deleteAssets([assetId], deleteWithoutTrash);
  };

  const handleRemove = () => {
    Logger.log('User', 'ThumbnailRemoveAssetFromProduct');
    removeAsset(assetId);
  };

  /**
   * Show spinner
   * @param {String} value
   */
  const runSpinner = (value) => {
    setShowSpinner(true);
    setSpinnerTitle(value || null);
  };

  const stopSpinner = () => {
    setShowSpinner(false);
    setSpinnerTitle(null);
  };

  useEffect(() => {
    if (uploadRevisionProgress === 0) {
      runSpinner('New revision is loading ');
    }

    if (uploadRevisionProgress === null) {
      stopSpinner();
    }
  }, [uploadRevisionProgress]);

  // it uses from placeholder config
  // eslint-disable-next-line no-unused-vars
  function handleRemoveNotFoundAssets() {
    removeNotFoundAssets([assetId]);
  }

  /** Remove lightboard */
  const handleRemoveLightboard = () => {
    const lightboardId = store.getState().router.location?.query?.lightboardId
      || lightboards[0]._id;
    const lightboard = lightboards.find((lb) => lb._id === lightboardId);
    if (lightboard) {
      Logger.log('User', 'ThumbnailRemoveLightboard');
      removeFromLightboard(lightboard, [assetId]);
    }
  };

  // needs for placeholder config
  const handlers = {
    handleDeleteForever,
    handleRemoveNotFoundAssets,
    handleRemoveLightboard,
  };

  /**
   * Set thumbnail placeholder
   * @param {Object} params
   */
  const renderPlaceholder = ({
    icon,
    iconColor,
    text,
    btn,
    isFailed,
    onClick,
    disableDownload,
    fullWidth,
    selectable,
  }) => {
    setThumbnailPlaceholder({
      icon,
      iconColor,
      text,
      btn,
      onClick: handlers[onClick] || null,
      fileName: name,
      disableDownload,
      fullWidth,
      selectable,
      isFailed,
    });
    setShowSpinner(false);
    setSpinnerTitle(null);
  };

  /**
   * Init thumbnail error
   * @param {Object} params
   */
  const initError = (params) => {
    const code = params && params.code;
    const config = configErrors[code] || getConfigDefaultPlaceholder();
    /** config.text may be a function */
    if (typeof config.text === 'function') config.text = config.text();
    const formatConfig = configFormats[extension];

    if (formatConfig && code !== 'noPermissions') {
      config.icon = formatConfig.icon || config.icon;
      config.iconColor = formatConfig.iconColor || config.iconColor;
      config.text = formatConfig.text
        || (typeof config.text === 'function' ? config.text() : config.text);
    } else {
      config.disableDownload = !isDownloadable;
    }

    if (poller.current) {
      poller.current.stop();
      poller.current = null;
    }

    renderPlaceholder(config);
  };

  const initImporting = ({ isFailed = false, productMedia = false }) => {
    let config = {};

    if (isFailed) {
      config = {
        text: 'Import failed',
        icon: () => <Warning width={50} height={50} />,
        iconColor: '#FF6B00',
        isFailed: true,
        disableDownload: true,
      };
    } else if (productMedia) {
      config = {
        text: 'Importing from Shopify store...',
        icon: () => <Shopify width={50} height={50} />,
        disableDownload: true,
      };
    } else {
      config = {
        text: 'Importing from external storage...',
        icon: () => <Cloud width={50} height={50} />,
        disableDownload: true,
      };
    }

    renderPlaceholder(config);
  };

  const initThumbnailing = (skipped) => {
    let config = { ...configErrors.thumbnailing };
    const formatConfig = configFormats[extension];

    if (formatConfig && ![ASYNC_JOB_STATUS_WAITING, ASYNC_JOB_STATUS_RUNNING].includes(thumbnailing)) {
      config.icon = formatConfig.icon || config.icon;
      config.iconColor = formatConfig.iconColor || config.iconColor;
      config.text = formatConfig.text || config.text;
    } else {
      config.disableDownload = !isDownloadable;
    }

    if (skipped) {
      config = getConfigDefaultPlaceholder();
    }

    renderPlaceholder(config);
  };

  const removePlaceholder = () => {
    setThumbnailPlaceholder(null);
    setShowSpinner(false);
    setSpinnerTitle(null);
  };

  const handleImgLoad = () => {
    /* if component mounted */
    if (catalogItemRef.current) {
      removePlaceholder();
      setThumbnailLoaded(true);
      if (poller.current) {
        poller.current.stop();
        poller.current = null;
      }
    }
  };

  const handleLoadImageError = () => {
    if ((mimeType || '').includes('google-apps')) {
      if (configGApps[mimeType]) {
        return renderPlaceholder({
          ...configGApps[mimeType],
          iconColor: '#474747',
        });
      }
      initError();
    }
    return null;
  };

  useEffect(() => {
    const thumbError = thumbnail && thumbnail.error;

    if (!hasAccess) {
      return initError({ code: 'noPermissions' });
    }
    if (isImportingInProcess) {
      return initImporting({ isFailed: false });
    }

    if (isImportingFailed) {
      return initImporting({ isFailed: true });
    }

    if (isImportingMediaInProcess) {
      return initImporting({ isFailed: false, productMedia: true });
    }

    if (isImportingMediaFailed) {
      return initImporting({ isFailed: true, productMedia: true });
    }

    if (isVideo && !isSupportedVideo && !customThumbnail) {
      /** if is not supported video */
      initError({ code: 205 });
    } else if (
      [ASYNC_JOB_STATUS_WAITING, ASYNC_JOB_STATUS_RUNNING].includes(
        thumbnailing,
      )
      || [ASYNC_JOB_STATUS_WAITING, ASYNC_JOB_STATUS_RUNNING].includes(crawling)
    ) {
      initThumbnailing();
    } else if (is3DModel || thumbError) {
      initError(thumbError);
    } else if (isEmpty) {
      initError({ code: '204' });
    } else if (
      (!thumbnail
      && thumbnailing === 'skipped'
      && thumbnailingReason
      && thumbnailingReason.includes('BY_ACCOUNT_PLAN_LIMITS')) || (isGoingToCopyConvert && !thumbnail)
    ) {
      initThumbnailing(true);
    } else if (thumbnail === null) {
      const size = Number(fileSize) || 0;
      const fieldToCheck = !Number.isNaN(size) && size > 504857600 ? storageCreatedAt : updatedAt; // > 480 MB
      if (storageType === 'gd' && dayjs(fieldToCheck).diff(dayjs(), 'hour') === 0) {
        /** if uploaded to GD less than an hour ago
         * if file is large (>480 MB) check createdAt instead of updatedAt */
        initThumbnailing();
        poller.current = pollGDThumbnail(assetId);
      } else {
        initError();
      }
    }
    if (ua.device.name === 'iPad') {
      initTouchListeners();
    }

    window.addEventListener('userChangeDateLocale', () => {
      forceUpdate((n) => !n);
    });
    return () => {
      if (ua.device.name === 'iPad') {
        removeTouchListeners();
      }

      if (poller.current) {
        poller.current.stop();
        poller.current = null;
      }
    };
    // Did mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasAccess) return;

    const hasInProgress = [
      ASYNC_JOB_STATUS_WAITING,
      ASYNC_JOB_STATUS_RUNNING,
    ].includes(thumbnailing);
    if (
      !hasInProgress
      && thumbnailing === ASYNC_JOB_STATUS_COMPLETE
      && thumbnail
    ) {
      removePlaceholder();
      return;
    }

    if (isVideo && !isSupportedVideo && !customThumbnail) {
      /** if is not supported video */
      if (thumbnailPlaceholder) return;
      initError({ code: 205 });
      return;
    }
    if (
      checkForThumbnailing({
        storageType,
        fileExtension,
        thumbnailing,
        thumbnail,
      })
    ) {
      if (
        thumbnailPlaceholder
        && thumbnailing !== 'waiting'
        && thumbnailing !== 'running'
      ) return;
      initThumbnailing();
      return;
    }

    if (thumbnail === null) {
      const size = Number(fileSize) || 0;
      const fieldToCheck = !Number.isNaN(size) && size > 504857600 ? storageCreatedAt : updatedAt; // > 480 MB
      if (
        thumbnailing === 'skipped'
        && thumbnailingReason
        && thumbnailingReason.includes('BY_ACCOUNT_PLAN_LIMITS')
      ) {
        initThumbnailing(true);
      } else if (
        [ASYNC_JOB_STATUS_WAITING, ASYNC_JOB_STATUS_RUNNING].includes(
          thumbnailing,
        )
        || [ASYNC_JOB_STATUS_WAITING, ASYNC_JOB_STATUS_RUNNING].includes(crawling)
      ) {
        initThumbnailing();
      } else if (
        [ASYNC_JOB_STATUS_WAITING, ASYNC_JOB_STATUS_RUNNING].includes(importing)
      ) {
        initImporting({ isFailed: false });
      } else if (
        [ASYNC_JOB_STATUS_FAILED, ASYNC_JOB_STATUS_REJECTED].includes(importing)
      ) {
        initImporting({ isFailed: true });
      } else if (
        [ASYNC_JOB_STATUS_WAITING, ASYNC_JOB_STATUS_RUNNING].includes(
          importingMedia,
        )
      ) {
        initImporting({ isFailed: false, productMedia: true });
      } else if (
        [ASYNC_JOB_STATUS_FAILED, ASYNC_JOB_STATUS_REJECTED].includes(
          importingMedia,
        )
      ) {
        initImporting({ isFailed: true, productMedia: true });
      } else if (
        !isEmpty
        && dayjs(fieldToCheck).diff(dayjs(), 'hour') === 0
        && storageType === 'gd'
      ) {
        initThumbnailing();
        if (!poller.current) poller.current = pollGDThumbnail(assetId);
      } else if (isEmpty) {
        initError({ code: '204' });
      } else {
        initError();
      }
    } else if (thumbnail) {
      if (thumbnail.error) initError(thumbnail.error);
      else removePlaceholder();
    }
  }, [
    thumbnail,
    thumbnailing,
    customThumbnail,
    crawling,
    importing,
    importingMedia,
  ]);

  const highlightAnimationReset = (type) => {
    removeHighlight([assetId], type);
  };

  const handleDownload = async () => {
    try {
      if (picsioConfig.isProofing) {
        await checkDownloadConsent();
      }
    } catch (err) {
      // user click Cancel on Dialog
      return;
    }

    Logger.log('User', 'ThumbnailDownload');
    showDownloadDialog([assetId]);
  };

  const handleMouseOver = async () => {
    if (ua.isMobileApp()) return;
    if (!isListViewMode) setMouseOver(true);
    if (
      isVideo
      && thumbnailLoaded
      && videoPreviews
      && videoPreviews.head
      && !videoThumbnail
      && videoThumbFetched === null
    ) {
      videoThumbFetched = false;
      if (isWebpSupported) {
        await getVideoThumbnail(assetId);
        videoThumbFetched = true;
      }
    }
  };

  const handleMouseLeave = () => {
    if (!isListViewMode) setMouseOver(false);
    const $cursorReorderImages = document.querySelector('.cursorReorderImages');
    if ($cursorReorderImages) {
      utils.css($cursorReorderImages, { display: 'none' });
    }
  };

  /**
   * Select item
   * @param {Boolean} shiftKey
   * @param {Boolean} altKey
   */
  const selectImage = (shiftKey, altKey) => {
    const selected = !isSelected;
    if (select) {
      if (isMediaMatch) {
        selectByMatch({
          value: selected,
          matchData: convertAssetToMatchData(asset),
          isRange: shiftKey,
          isOnlyOne: altKey,
        });
        return;
      }
      if (picsioConfig.isProofing && !downloadFiles) return;

      select({
        id: assetId,
        value: selected,
        isRange: shiftKey,
        isOnlyOne: altKey,
      });
    }
  };

  /**
   * Select item
   * @param {MouseEvent} event
   */
  const handleSelectItem = (event) => {
    event.stopPropagation();
    event.preventDefault();
    selectImage(event.shiftKey, event.altKey);
  };

  /**
   * Click on this.el
   * @param {MouseEvent} event
   */
  const handleClick = (event) => {
    const isControls = event.target.classList.contains('catalogItem__controls');
    const isName = event.target.classList.contains('catalogItem__name');
    const isChips = event.target.classList.contains('catalogItem__chips');
    const isCollectionsWrapper = event.target.classList.contains(
      'catalogItem__collections-wrapper',
    );

    if (
      (event.target === event.currentTarget
        || isControls
        || isName
        || isChips
        || isCollectionsWrapper)
      && (picsioConfig.isMainApp || picsioConfig.isProofing)
    ) {
      selectImage(event.shiftKey, event.altKey);
    }
  };

  const openPreview = (id) => {
    navigate(`/preview/${id}`);
    Logger.log('User', 'OpenAssetFromCatalog', {
      search: { position: number },
    });
  };

  /**
   * Click on media item
   * @param {MouseEvent} event
   */
  const handleClickImage = (event) => {
    event.stopPropagation();

    if (
      picsioConfig.isMainApp
      && (event.ctrlKey || event.metaKey || event.shiftKey)
    ) {
      selectImage(event.shiftKey);
    } else if (picsioConfig.isMainApp && event.altKey) {
      selectImage(null, event.altKey);
    } else if (hasAccess) {
      openPreview(assetId);
    }
  };

  /**
   * Add new revision
   * @param {File} file
   */
  const addRevision = async (file) => {
    // runSpinner('New revision is loading ');
    const { items } = store.getState().assets;
    const currentAsset = items.find((item) => item._id === assetId);
    await revisionUploader(file, currentAsset);
    // stopSpinner();
  };

  const handleUploadClick = () => {
    Logger.log('User', 'ThumbnailAddRevision');
    inputFileRef.current.click();
  };

  /**
   * On change input file
   * @param {Event} event
   */
  const handleAddRevision = (event) => {
    if (event && event.target) {
      addRevision(event.target.files[0]);
      // event.target.value = '';
    }
  };

  /**
   * Check user permission to detach collection from asset
   * @param {string} path
   * @returns boolean
   */
  const checkPermissionToDetachCollection = (path) => {
    const { editAssetCollections } = PERMISSIONS;
    return permissionChecker.checkPermissionByPath(editAssetCollections, path);
  };

  const searchByImage = () => {
    Logger.log('User', 'ThumbnailFindSimilarImagesClicked', {
      view_mood: isListViewMode
        ? 'list'
        : 'grid',
      reference_assetId: assetId,
      file_format: fileExtension,
    });
    setSearchRoute({
      ...searchProps,
      collectionIds: collections?.my?._id,
      byImageId: assetId,
      similarityThreshold: searchProps?.similarityThreshold || 0.55,
    });
  };

  const catalogItemChipsStyles = {};
  if (isListViewMode) {
    let catalogItemButtonsWidth = 40;
    if (allowAssetSharing) catalogItemButtonsWidth += 37;
    if (uploadFiles) catalogItemButtonsWidth += 37;
    if (allowDuplicateAsset) catalogItemButtonsWidth += 37;
    if (downloadFiles) catalogItemButtonsWidth += 37;
    if (enableEditor) catalogItemButtonsWidth += 37;
    if (isLightboardsView) catalogItemButtonsWidth += 37;
    if (picsioConfig.isMainApp && isTrashed) catalogItemButtonsWidth += 74;
    if (removeFiles) catalogItemButtonsWidth += 37;
    catalogItemChipsStyles.right = catalogItemButtonsWidth;
  }

  return (
    <StyledCatalogItem
      ref={catalogItemRef}
      className={cn('catalogItem', {
        like: flag === 'flagged',
        dislike: flag === 'rejected',
        showStars: rating > 0,
        trashed: isTrashed,
        isSelected,
        isAssetBusy,
        noTitle: !title,
        withTitle: isListViewMode && title,
        isNarrow: !isListViewMode && styles.width < 166,
        isOdd: isListViewMode && isOdd && !isMediaMatch,
        isVideo,
        isMediaMatch,
      })}
      $styles={styles}
      draggable={
        hasAccess
        && !isAssetBusy
        && !archived
        && !isImportingInProcess
        && !isImportingFailed
        && !isImportingMediaInProcess
        && !isImportingMediaFailed
        && !isMediaMatch
      }
      onClick={
        isAssetBusy
          || isImportingInProcess
          || isImportingFailed
          || isImportingMediaInProcess
          || isImportingMediaFailed
          ? null
          : handleClick
      }
      onDrop={(event) => handleDrop(
        event,
        isListViewMode,
        number,
        reorder,
        activeCollectionSortType,
        activeCollectionIds,
      )}
      onDragOver={(event) => handleDragOver(event, isListViewMode, styles)}
      onDragLeave={handleDragLeave}
      onMouseOver={isAssetBusy ? null : handleMouseOver}
      onMouseLeave={handleMouseLeave}
      onMouseDown={(event) => handleMouseDown(event, catalogItemRef, trashed)}
      onMouseUp={handleMouseUp}
      onDragStart={(event) => handleDragStart(
        event,
        assetId,
        isSelected,
        select,
        downloadFiles,
        isProductView,
      )}
      onDragEnd={handleDragEnd}
      onContextMenu={handleContextMenu}
    >
      <If
        condition={
          !isImportingInProcess
          && !isImportingFailed
          && !isImportingMediaInProcess
          && !isImportingMediaFailed
        }
      >
        <If condition={uploadFiles}>
          <div className="catalogItem__file">
            <input
              ref={inputFileRef}
              type="file"
              onChange={handleAddRevision}
            />
          </div>
        </If>
        <If condition={flag && flagShow && hasAccess && !isMediaMatch}>
          <Flag
            assetId={assetId}
            flag={flag}
            flagChangeable={flagChangeable}
            highlight={paramsForHighlight.includes('flag')}
            highlightAnimationReset={highlightAnimationReset}
          />
        </If>
        <If condition={isExternal}>
          <ButtonExternal
            url={asset.source}
            favicon={asset.crawledData?.logo}
          />
        </If>
        <If
          condition={
            (picsioConfig.isMainApp
              || (picsioConfig.isProofing && downloadFiles))
            && select
            && (isMobile || mouseOver || isListViewMode || isSelected)
          }
        >
          <Checkbox
            hasAccess={hasAccess}
            isSelected={isSelected}
            onClick={handleSelectItem}
          />
        </If>
        <If
          condition={
            ratingShow
            && (mouseOver || rating > 0 || isListViewMode)
            && !isMediaMatch
          }
        >
          <Stars
            assetId={assetId}
            currentValue={rating}
            ratingChangeable={ratingChangeable}
            highlight={paramsForHighlight.includes('rating')}
            highlightAnimationReset={highlightAnimationReset}
          />
        </If>
        <If condition={colorShow && color && !isMediaMatch}>
          <Color
            color={color}
            highlight={paramsForHighlight.includes('color')}
            highlightAnimationReset={highlightAnimationReset}
            colorAt={colorAt}
            colorBy={colorBy}
          />
        </If>
        <If
          condition={
            picsioConfig.isMainApp
            && !isListViewMode
            && assignees.length
            && !isMediaMatch
          }
        >
          <Assignees assignees={assignees} max={3} />
        </If>
      </If>
      <If condition={isAssetBusy}>
        <PlaceholderAssetBusy
          isGoingToTrash={isGoingToTrash}
          isGoingToDelete={isGoingToDelete}
          isGoingToRestore={isGoingToRestore}
          isGoingToMove={isGoingToMove}
          isGoingToCopy={isGoingToCopy}
          isGoingToCopyConvert={isGoingToCopyConvert}
          assetName={asset.name}
        />
      </If>

      <If
        condition={
          !isImportingInProcess
          && !isImportingFailed
          && !isImportingMediaInProcess
          && !isImportingMediaFailed
        }
      >
        <Choose>
          <When condition={isListViewMode}>
            <If condition={isRestricted}>
              <Restriction restrictedReason={restrictedReason} />
            </If>
          </When>
          <Otherwise>
            <div className="catalogItem__additional">
              <If
                condition={
                  commentsShow && !commentsHidden && commentsEnable && (isHovering || newComments)
                }
              >
                <Comments
                  newCommentsLength={newComments}
                  assetId={assetId}
                  allCommentsLength={comments.length}
                  highlight={paramsForHighlight.includes('newComments')}
                  highlightAnimationReset={highlightAnimationReset}
                />
              </If>
              <If condition={isRestricted}>
                <Restriction restrictedReason={restrictedReason} />
              </If>
              <If condition={archived}>
                <Archived
                  archivedReason={
                    archivedByReason
                    || localization.DETAILS.defaultArchiveReason
                  }
                />
              </If>
              <If condition={asset.transcripts && isHovering}>
                <Transcription assetId={assetId} />
              </If>
            </div>
            <If condition={revisionsShow && newRevisions}>
              <Revisions
                newRevisionsLength={newRevisions}
                assetId={assetId}
                highlight={paramsForHighlight.includes('newRevisions')}
                highlightAnimationReset={highlightAnimationReset}
              />
            </If>
          </Otherwise>
        </Choose>
      </If>

      <If condition={foundWithVisualSearch}>
        <Tooltip content={localization.CATALOG_ITEM.tooltipFoundWithVisualSearch(foundWithVisualSearchText, foundWithVisualSearchCLIP)}>
          <div className={cn('catalogItem__visualSearchIconBox', isListViewMode && 'catalogItem__visualSearchIconBoxListView')}>
            <Icon className="catalogItem__visualSearchIcon" color="primary-contrast">
              <AiEngine />
            </Icon>
          </div>
        </Tooltip>
      </If>

      <If condition={
        (CLIPEmbedding === 'complete')
        && !picsioConfig.isPim
        && picsioConfig.isMainApp
        && enableVisualSearchSetting
        && visualSearchFeature
        && isHovering
      }
      >
        <div
          className={cn('catalogItem__imageSearchIconBox', isListViewMode && 'catalogItem__imageSearchIconBoxListView')}
          role="presentation"
          data-testid="findSimilarImagesIcon"
          onClick={searchByImage}
        >

          <Tooltip content={localization.CATALOG_ITEM.tooltipFindSimilarImage}>
            <Icon className="catalogItem__imageSearchIcon" color="primary">
              <FindImage />
            </Icon>
          </Tooltip>

        </div>
      </If>

      <CSSTransition
        unmountOnExit
        in={Boolean(
          showTileHint && hasAccess && (isHovering || isListViewMode),
        )}
        timeout={300}
        classNames="fade"
        nodeRef={controlsRef}
      >
        <Controls
          ref={controlsRef}
          allowDuplicateAsset={allowDuplicateAsset}
          allowRemoveTags={allowRemoveTags}
          numberOfCollections={tags?.length}
          assetId={assetId}
          flagShow={flagShow}
          colorShow={colorShow}
          commentsEdit={commentsEdit}
          commentsEnable={commentsEnable}
          fileNameShow={isExternal && !isListViewMode ? false : fileNameShow}
          allowAssetSharing={allowAssetSharing}
          uploadFiles={uploadFiles}
          downloadFiles={downloadFiles}
          enableEditor={enableEditor}
          removeFiles={removeFiles}
          storageType={storageType}
          isListViewMode={isListViewMode}
          storageId={storageId}
          name={name}
          shortName={shortName}
          fileExtension={fileExtension}
          title={title}
          color={color}
          colorAt={colorAt}
          colorBy={colorBy}
          colorChangeable={colorChangeable}
          trashAssets={() => trashAssets([assetId])}
          restoreAssets={() => restoreAssets([assetId])}
          handleDownload={handleDownload}
          handleUploadClick={handleUploadClick}
          handleRemoveLightboard={handleRemoveLightboard}
          isLightboardsView={isLightboardsView}
          isAssetFromInbox={isAssetFromInbox}
          isShared={isShared}
          assetSharing={assetSharing}
          isGoogleDriveDocument={isGoogleDriveDocument}
          mimeType={mimeType}
          isEditableInPicsioEditor={isEditableInPicsioEditor}
          revisionsLength={revisions.length}
          isMobileView={isMobileView}
          itemWidth={styles.width}
          itemHeight={styles.height}
          isTrashed={isTrashed}
          deleteForever={handleDeleteForever}
          remove={handleRemove}
          duplicateAssets={duplicateAssets}
          generatedDate={generatedDate}
          createdDate={createdDate}
          isImportingFailed={isImportingFailed}
          isImportingMediaFailed={isImportingMediaFailed}
          isImportingInProcess={
            isImportingInProcess || isImportingMediaInProcess
          }
          retryImporting={retryImporting}
          isProductView={isProductView}
          isMatchingView={isMediaMatch}
          removeDisabled={removeDisabled}
          removeTooltip={removeTooltip}
        />
      </CSSTransition>

      <If condition={isListViewMode && (title || description)}>
        <TitleDescription
          title={title}
          description={description}
          isMobileView
          hideTitle={(picsioConfig.isProofing && !picsioConfig.access?.titleShow) || hiddenFields.includes('title')}
          hideDescription={
            (picsioConfig.isProofing && !picsioConfig.access?.descriptionShow) || hiddenFields.includes('description')
          }
        />
      </If>

      <If
        condition={
          !isListViewMode
          && duration
          && isVideo
          && !isImportingInProcess
          && !isImportingMediaInProcess
        }
      >
        <Duration duration={duration} />
      </If>

      <If
        condition={
          videoProgress && !isImportingInProcess && !isImportingMediaInProcess
        }
      >
        <VideoProgress progress={videoProgress} duration={duration} />
      </If>

      <CSSTransition
        unmountOnExit
        in={Boolean(
          picsioConfig.isMainApp
          && (tags.length || lightboards.length || keywords.length || isMediaMatch)
          && (isHovering || isListViewMode),
        )}
        timeout={300}
        classNames="fade"
        nodeRef={chipsRef}
      >
        <ChipsWrapper ref={chipsRef} className="catalogItem__chips" style={catalogItemChipsStyles}>
          <If
            condition={
              !isListViewMode && lightboards.length && isAllowedLightboards
            }
          >
            <div className="catalogItem__chips-chip">
              <Lightboards
                assetId={assetId}
                items={lightboards}
                onRemove={removeFromLightboard}
              />
            </div>
          </If>
          <If condition={!isListViewMode && keywords.length}>
            <div className="catalogItem__chips-chip">
              <Keywords
                assetId={assetId}
                items={keywords}
                keywordsEditable={false}
                onRemove={detachKeyword}
              />
            </div>
          </If>
          <If condition={tags.length || isMediaMatch}>
            <div className={cn('catalogItem__chips-chip', {
              'catalogItem__chips-chip--media-match': isMediaMatch,
              'catalogItem__chips--image-order': true,
            })}
            >
              <If condition={!isVariant && picsioConfig.isPim && isProductView && showImageOrder}>
                <ImageOrder
                  order={findGalleryOrderEntry(asset, productId, viewContext?.layer)}
                  productId={productId}
                  productAssetsLength={productAssetsLength}
                  assetId={assetId}
                  disabled={isChangingOrder || imageOrderDisabled}
                  isImage={isImage}
                  tooltip={imageOrderTooltip}
                  viewContext={viewContext}
                />
              </If>
              <Collections
                assetId={assetId}
                archived={archived}
                items={tags}
                allowRemoveTags={allowRemoveTags && !isMediaMatch}
                isMediaMatch={isMediaMatch}
                checkPermissionToDetachCollection={
                  checkPermissionToDetachCollection
                }
              />
            </div>
          </If>
          <If condition={product && isMediaMatch}>
            <div className="catalogItem__chips-chip">
              <Icon className="catalogItem__chips-icon">
                <AutoSuggestDown />
              </Icon>
              <Tag
                type="product"
                text={
                  product.type === 'variant'
                    ? product.displayName
                    : product.title
                }
                icon={<Product />}
                iconColor={
                  product.type === 'variant'
                    ? VARIANT_COLOR
                    : colorsByStatus[product.status]
                }
              />
            </div>
          </If>
        </ChipsWrapper>
      </CSSTransition>

      {/* thumbnail */}
      <Choose>
        <When condition={thumbnailPlaceholder !== null}>
          <MediaPlaceholder
            thumbnailPlaceholder={thumbnailPlaceholder}
            isVideo={isVideo}
            onClickImage={
              isImportingInProcess
                || isImportingFailed
                || isImportingMediaInProcess
                || isImportingMediaFailed
                ? null
                : handleClickImage
            }
          />
        </When>
        <Otherwise>
          <Media
            watermark={watermark}
            number={number}
            width={styles.width}
            name={name}
            isVariant={isVariant}
            fileExtension={fileExtension}
            thumbnailLoaded={thumbnailLoaded}
            isListViewMode={isListViewMode}
            isSelected={isSelected}
            isTransparentImageLoaded={isTransparentImageLoaded}
            videoThumbnail={videoThumbnail}
            mouseOver={mouseOver}
            url={url}
            urlSmallThumb={urlSmallThumb}
            description={description}
            userOrientation={userOrientation}
            imageMediaMetadata={imageMediaMetadata}
            isVideo={isVideo}
            handleImgLoad={handleImgLoad}
            handleLoadImageError={handleLoadImageError}
            handleClickImage={handleClickImage}
            makeTransformForImage={makeTransformForImage}
            foundWithVisualSearch={foundWithVisualSearch}
            isProductView={isProductView}
          />
        </Otherwise>
      </Choose>

      {/* spinner */}
      <If condition={showSpinner || inProgress}>
        <Spinner
          spinnerTitle={spinnerTitle}
          uploadRevisionProgress={uploadRevisionProgress}
        />
      </If>
    </StyledCatalogItem>
  );
}

CatalogItem.propTypes = {
  asset: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    allowAssetSharing: PropTypes.bool,
    archived: PropTypes.bool,
    archivedByReason: PropTypes.string,
    color: PropTypes.string,
    colorChangeable: PropTypes.bool,
    colorShow: PropTypes.bool,
    downloadFiles: PropTypes.bool,
    enableEditor: PropTypes.bool,
    fileExtension: PropTypes.string,
    fileNameShow: PropTypes.bool,
    flag: PropTypes.string,
    flagChangeable: PropTypes.bool,
    flagShow: PropTypes.bool,
    hasAccess: PropTypes.bool.isRequired,
    isGoingToDelete: PropTypes.bool,
    isGoingToMove: PropTypes.bool,
    isGoingToCopy: PropTypes.bool,
    isGoingToRestore: PropTypes.bool,
    isGoingToTrash: PropTypes.bool,
    isShared: PropTypes.bool,
    isTrashed: PropTypes.bool,
    name: PropTypes.string.isRequired,
    paramsForHighlight: PropTypes.arrayOf(PropTypes.string),
    rating: PropTypes.number,
    ratingChangeable: PropTypes.bool,
    ratingShow: PropTypes.bool,
    removeFiles: PropTypes.bool,
    shortName: PropTypes.string,
    storageId: PropTypes.string,
    storageType: PropTypes.string.isRequired,
    title: PropTypes.string,
    uploadFiles: PropTypes.bool,
    commentsShow: PropTypes.bool,
    commentsEnable: PropTypes.bool,
    transcripts: PropTypes.arrayOf(PropTypes.any),
    // commentsEdit: PropTypes.bool,
    // revisionsShow: PropTypes.bool,
  }).isRequired,
  assetsActions: PropTypes.shape({
    deleteAssets: PropTypes.func.isRequired,
    detachKeyword: PropTypes.func.isRequired,
    removeFromLightboard: PropTypes.func.isRequired,
    removeHighlight: PropTypes.func.isRequired,
    removeNotFoundAssets: PropTypes.func.isRequired,
    removeAsset: PropTypes.func,
    reorder: PropTypes.func.isRequired,
    restoreAssets: PropTypes.func.isRequired,
    select: PropTypes.func.isRequired,
    selectByMatch: PropTypes.func.isRequired,
    trashAssets: PropTypes.func.isRequired,
    duplicateAssets: PropTypes.func.isRequired,
    retryImporting: PropTypes.func.isRequired,
  }).isRequired,
  inProgress: PropTypes.bool,
  isListViewMode: PropTypes.bool.isRequired,
  isMobileView: PropTypes.bool.isRequired,
  isOdd: PropTypes.bool,
  number: PropTypes.number.isRequired,
  styles: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    translateX: PropTypes.number.isRequired,
    translateY: PropTypes.number.isRequired,
  }).isRequired,
  isLightboardsView: PropTypes.bool,
  isProductView: PropTypes.bool,
  showImageOrder: PropTypes.bool,
  imageOrderDisabled: PropTypes.bool,
  imageOrderTooltip: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.func,
  ]),
  viewContext: PropTypes.shape({
    masterProductId: PropTypes.string,
    listingProductId: PropTypes.string,
    channelId: PropTypes.string,
    layer: PropTypes.string,
  }),
  removeDisabled: PropTypes.bool,
  removeTooltip: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.func,
  ]),
};

const StyledCatalogItem = styled.div.attrs((props) => ({
  style: {
    transform: `translate3d(${props.$styles.translateX}px, ${props.$styles.translateY}px, 0)`,
    width: props.$styles.width,
    height: props.$styles.height,
  },
}))``;

export default memo(CatalogItem);
